import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Ticket, Trophy, Star, SpeakerHigh, SpeakerSlash, ShieldCheck, UserPlus, IdentificationCard, Confetti, LinkSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { trackLogin, trackRegistration } from '@/analytics';
import { API } from '@/App';
import TicketCounter from '@/components/TicketCounter';
import AcceptedPaymentNetworks from '@/components/AcceptedPaymentNetworks';
import HallOfFame from '@/components/HallOfFame';
import VerificationWizard from '@/components/VerificationWizard';
import BrandLogo from '@/components/BrandLogo';
import { Checkbox } from '@/components/ui/checkbox';
import PasswordInput from '@/components/PasswordInput';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { getPolicyLinks } from '@/content/policies';
import { useUnlockableAudio } from '@/hooks/useUnlockableAudio';


import { useLanguage } from '@/i18n/LanguageContext';
import { getT } from '@/i18n/translations';

const Landing = ({ onLogin, isMuted, toggleMute }) => {
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const { language } = useLanguage();
  const t = getT('landing', language);
  const [showVerification, setShowVerification] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { unlocked: audioUnlocked, unlockNow: unlockAudio } = useUnlockableAudio(
    'https://customer-assets.emergentagent.com/job_chance-games-7/artifacts/gznyo98i_Selkor%20Studio%20-%20The%20Horror%20Within%20-%20Heart%20Pounding%20Frantic.wav',
    { loop: true, baseVolume: 1.0, startAt: 2, isMuted }
  );
  
  const [loginData, setLoginData] = useState({ email: '', password: '', totpCode: '' });
  const [requires2FA, setRequires2FA] = useState(false);
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    username: '',
    phone: '',
    termsAccepted: false,
    affiliateCode: '',
  });

  useEffect(() => {
    // If user lands via /?ref=CODE — capture as affiliate partner code (QR / launch slot).
    // Pattern: 3-64 chars, A-Z, 0-9, _ or -. Backend validates against affiliates collection.
    try {
      const params = new URLSearchParams(window.location.search);
      const refRaw = (params.get('ref') || '').trim().toUpperCase();
      const refAffiliate = /^[A-Z0-9_-]{3,64}$/.test(refRaw) ? refRaw : '';

      if (refAffiliate) {
        setRegisterData((prev) => ({ ...prev, affiliateCode: refAffiliate }));
        try { sessionStorage.setItem('pendingAffiliateCode', refAffiliate); } catch (_) { /* private mode */ }
        setAuthMode('register');
        setShowAuth(true);
      } else {
        const storedAff = sessionStorage.getItem('pendingAffiliateCode');
        if (storedAff && !registerData.affiliateCode) {
          setRegisterData((prev) => ({ ...prev, affiliateCode: storedAff }));
        }
      }
    } catch (refCaptureErr) {
      console.warn('Affiliate capture from URL failed:', refCaptureErr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // t is provided by getT('landing', language) above

  const readJsonOnce = async (response) => {
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const playHoverSound = () => {
    if (isMuted) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create oscillator for wind-like sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const filterNode = audioContext.createBiquadFilter();
      
      // Configure filter for storm/wind effect
      filterNode.type = 'lowpass';
      filterNode.frequency.setValueAtTime(800, audioContext.currentTime);
      filterNode.Q.setValueAtTime(1, audioContext.currentTime);
      
      // Create noise for storm effect (3 seconds duration)
      const bufferSize = audioContext.sampleRate * 3.0;
      const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
      }
      
      const noise = audioContext.createBufferSource();
      noise.buffer = noiseBuffer;
      
      // Configure oscillator with 3 second sweep
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(60, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + 3.0);
      
      // Configure volume envelope with longer fade (3 seconds)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.12, audioContext.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0.12, audioContext.currentTime + 2.3);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 3.0);
      
      // Connect nodes
      oscillator.connect(filterNode);
      noise.connect(filterNode);
      filterNode.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Play sound
      oscillator.start(audioContext.currentTime);
      noise.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 3.0);
      noise.stop(audioContext.currentTime + 3.0);
      
      // Clean up
      setTimeout(() => {
        audioContext.close();
      }, 3200);
    } catch (error) {
      console.log('Audio not supported or blocked');
    }
  };

  const playGetStartedSound = () => {
    if (isMuted) return;
    try {
      const audio = new Audio('https://customer-assets.emergentagent.com/job_chance-games-7/artifacts/p8k583es_Artlist%20Studios%20-%20Braamagog%20-%20Low%20Braam.wav');
      audio.volume = 1.0;
      audio.play().catch(err => console.log('Audio playback blocked:', err));
    } catch (error) {
      console.log('Audio not supported or blocked');
    }
  };

  const playLoginSound = () => {
    if (isMuted) return;
    try {
      const audio = new Audio('https://customer-assets.emergentagent.com/job_chance-games-7/artifacts/n2bi0us0_Asak%20SFX%20-%20Sci%20Fi%20Cinematics%20-%20Time%20Freeze%20Swirl.wav');
      audio.volume = 1.0;
      audio.play().catch(err => console.log('Audio playback blocked:', err));
    } catch (error) {
      console.log('Audio not supported or blocked');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    playLoginSound();
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      
      const data = await readJsonOnce(response);
      if (response.ok) {
        // Already-registered user: always proceed to dashboard.
        // KYC gate is now enforced inside the dashboard on the Buy-Ticket button,
        // so we never trap a returning user in the verification wizard after a
        // simple login or password reset.
        onLogin(data.token, data.user);
        trackLogin('email');
        toast.success('Welcome back!');
        setRequires2FA(false);
      } else {
        // 2FA gate: when admin has TOTP enabled, server returns 401 with requires2FA flag.
        const detail = typeof data.detail === 'object' ? data.detail : null;
        if (detail?.requires2FA) {
          setRequires2FA(true);
          // If user already had typed a wrong code, surface the error; otherwise just prompt.
          if (loginData.totpCode) {
            toast.error('Invalid 2FA code — try again');
          } else {
            toast.info('Two-factor code required');
          }
        } else {
          toast.error((detail?.error) || (typeof data.detail === 'string' ? data.detail : 'Login failed'));
        }
      }
    } catch (error) {
      toast.error('Login failed');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerData)
      });
      
      const data = await readJsonOnce(response);
      if (response.ok) {
        setPendingToken(data.token);
        setShowAuth(false);
        setShowVerification(true);
        trackRegistration('email');
        toast.success('Account created! Complete verification.');
        // Clear pending ref / affiliate codes after successful registration
        try {
          sessionStorage.removeItem('pendingReferralCode');
          sessionStorage.removeItem('pendingAffiliateCode');
          document.cookie = 'affiliate_ref=; Max-Age=0; path=/';
        } catch (_) { /* non-fatal */ }
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Static Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/b8211326-9b7b-4256-be19-bc949e555ee9/images/c8e58fddc7990a08708c1808dda4062394df9bff12066f51535b64f5f235f496.png)'
        }}
      />
      <div className="absolute inset-0 bg-[#0e0b04]/60" />
      
      {/* Floating Golden Particles with Mouse Parallax */}
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={`gold-particle-${i}`}
          className="absolute w-2 h-2 bg-[#cba135] rounded-full opacity-30"
          style={{
            left: `${(i * 8 + 5) % 100}%`,
            top: `${(i * 13 + 10) % 100}%`,
            boxShadow: '0 0 10px rgba(203, 161, 53, 0.5)'
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.2, 0.6, 0.2],
            scale: [1, 1.5, 1]
          }}
          transition={{
            duration: 3 + i * 0.5,
            repeat: Infinity,
            delay: i * 0.3
          }}
          initial={{ x: 0, y: 0 }}
          style={{
            x: (mousePosition.x - window.innerWidth / 2) * (0.01 + i * 0.002),
            y: (mousePosition.y - window.innerHeight / 2) * (0.01 + i * 0.002) - scrollY * 0.3
          }}
        />
      ))}
      
      <div className="relative z-10">
        <header className="bg-[#0e0b04]/70 backdrop-blur-xl border-b border-[#cba135]/10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
            <BrandLogo />
            <div className="flex items-center gap-6">
              <Button
                data-testid="mute-button"
                onClick={() => { unlockAudio(); toggleMute(); }}
                variant="outline"
                size="icon"
                className="border-[#cba135]/20 text-[#cba135] hover:bg-[#cba135]/10 hover:text-[#cba135] bg-[#1a1508]"
              >
                {isMuted ? (
                  <SpeakerSlash size={20} weight="fill" />
                ) : (
                  <SpeakerHigh size={20} weight="fill" />
                )}
              </Button>
              <Button 
                data-testid="header-login-button"
                onClick={() => { 
                  playLoginSound();
                  setAuthMode('login'); 
                  setShowAuth(true); 
                }}
                className="bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold tracking-wide hover:brightness-110 transition-all shadow-[0_0_15px_rgba(203,161,53,0.2)] hover:shadow-[0_0_25px_rgba(203,161,53,0.5)]"
              >
                {t.login}
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-[#cba135] mb-6">
              {t.hero} <motion.span 
                className="gold-shimmer font-semibold inline-block"
                animate={{ 
                  scale: [1, 1.08, 1, 1.08, 1, 1, 1, 1],
                  textShadow: [
                    '0 0 20px rgba(203, 161, 53, 0.3)',
                    '0 0 40px rgba(203, 161, 53, 0.8)',
                    '0 0 20px rgba(203, 161, 53, 0.3)',
                    '0 0 40px rgba(203, 161, 53, 0.8)',
                    '0 0 20px rgba(203, 161, 53, 0.3)',
                    '0 0 15px rgba(203, 161, 53, 0.2)',
                    '0 0 15px rgba(203, 161, 53, 0.2)',
                    '0 0 20px rgba(203, 161, 53, 0.3)'
                  ]
                }}
                transition={{ 
                  duration: 2.4,
                  repeat: Infinity,
                  times: [0, 0.1, 0.2, 0.3, 0.4, 0.6, 0.8, 1],
                  ease: "easeInOut"
                }}
              >
                €200,000
              </motion.span>
            </h2>
            <motion.p 
              className="text-base leading-relaxed text-[#cba135]/70 max-w-2xl mx-auto mb-8 relative overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent pointer-events-none"
                initial={{ x: '-100%', scaleX: 0.5 }}
                animate={{ x: '200%', scaleX: 0.5 }}
                transition={{ 
                  duration: 1.2, 
                  delay: 1,
                  repeat: Infinity,
                  repeatDelay: 13,
                  ease: [0.65, 0, 0.35, 1]
                }}
                style={{ 
                  filter: 'blur(2px)',
                  mixBlendMode: 'overlay',
                  opacity: 0.9
                }}
              />
              <span className="relative z-10">
                {t.heroDesc}
                <br />{t.heroDesc2}
              </span>
            </motion.p>
            <motion.div
              whileHover={{ scale: 1.08 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
            >
              <Button 
                data-testid="get-started-button"
                onClick={() => { 
                  playGetStartedSound();
                  setAuthMode('register'); 
                  setShowAuth(true); 
                }}
                size="lg"
                className="bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold tracking-wide hover:brightness-110 transition-all shadow-[0_0_15px_rgba(203,161,53,0.2)] hover:shadow-[0_0_25px_rgba(203,161,53,0.5)] px-12 py-6 text-lg rounded-full"
              >
                <Star size={24} weight="fill" className="mr-2" />
                {t.claimSpot}
              </Button>
            </motion.div>
          </motion.div>

          <TicketCounter />

          <AcceptedPaymentNetworks />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-24"
          >
            <motion.div 
              className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] p-8 rounded-lg relative overflow-hidden cursor-pointer"
              whileHover={{ 
                scale: 1.02,
                borderColor: 'rgba(203, 161, 53, 0.3)',
                boxShadow: '0 12px 40px rgba(203, 161, 53, 0.2)',
                y: -5
              }}
              transition={{ duration: 0.3 }}
              style={{
                transform: `translateY(${scrollY * 0.1}px)`,
                x: (mousePosition.x - window.innerWidth / 2) * 0.01,
                y: (mousePosition.y - window.innerHeight / 2) * 0.01
              }}
            >
              <div className="shimmer-overlay" />
              <Ticket size={48} weight="duotone" className="text-[#cba135] mb-4 relative z-10" />
              <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135] mb-3 relative z-10">{t.limitedPool}</h3>
              <p className="text-base leading-relaxed text-[#cba135]/70 relative z-10">{t.limitedPoolDesc}</p>
            </motion.div>
            
            <motion.div 
              className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] p-8 rounded-lg relative overflow-hidden cursor-pointer"
              whileHover={{ 
                scale: 1.02,
                borderColor: 'rgba(203, 161, 53, 0.3)',
                boxShadow: '0 12px 40px rgba(203, 161, 53, 0.2)',
                y: -5
              }}
              transition={{ duration: 0.3 }}
              style={{
                transform: `translateY(${scrollY * 0.15}px)`,
                x: (mousePosition.x - window.innerWidth / 2) * 0.015,
                y: (mousePosition.y - window.innerHeight / 2) * 0.015
              }}
            >
              <div className="shimmer-overlay" />
              <Trophy size={48} weight="duotone" className="text-[#cba135] mb-4 relative z-10" />
              <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135] mb-3 relative z-10">{t.instantDraw}</h3>
              <p className="text-base leading-relaxed text-[#cba135]/70 relative z-10">{t.instantDrawDesc}</p>
            </motion.div>

            <motion.div 
              className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] p-8 rounded-lg relative overflow-hidden cursor-pointer"
              whileHover={{ 
                scale: 1.02,
                borderColor: 'rgba(203, 161, 53, 0.3)',
                boxShadow: '0 12px 40px rgba(203, 161, 53, 0.2)',
                y: -5
              }}
              transition={{ duration: 0.3 }}
              style={{
                x: (mousePosition.x - window.innerWidth / 2) * 0.025,
                y: (mousePosition.y - window.innerHeight / 2) * 0.025
              }}
            >
              <div className="shimmer-overlay" />
              <ShieldCheck size={48} weight="duotone" className="text-[#cba135] mb-4 relative z-10" />
              <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135] mb-3 relative z-10">{t.rng}</h3>
              <p className="text-base leading-relaxed text-[#cba135]/70 relative z-10">{t.rngDesc}</p>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-24 max-w-4xl mx-auto"
            data-testid="join-now-section"
          >
            <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#cba135] mb-12 text-center gold-shimmer">
              {t.joinNowTitle}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] p-8 rounded-lg text-center relative overflow-hidden"
                whileHover={{ 
                  scale: 1.02,
                  borderColor: 'rgba(203, 161, 53, 0.3)',
                  boxShadow: '0 12px 40px rgba(203, 161, 53, 0.2)',
                  y: -5
                }}
                transition={{ duration: 0.3 }}
              >
                <div className="shimmer-overlay" />
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-full bg-[#cba135]/10 border border-[#cba135]/20 flex items-center justify-center mx-auto mb-4">
                    <span className="heading-font text-xl font-semibold text-[#cba135]">1</span>
                  </div>
                  <UserPlus size={40} weight="duotone" className="text-[#cba135] mx-auto mb-4" />
                  <h3 className="heading-font text-lg font-medium text-[#cba135] mb-3">{t.joinStep1Title}</h3>
                  <p className="text-sm leading-relaxed text-[#cba135]/70">{t.joinStep1Desc}</p>
                </div>
              </motion.div>

              <motion.div
                className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] p-8 rounded-lg text-center relative overflow-hidden"
                whileHover={{ 
                  scale: 1.02,
                  borderColor: 'rgba(203, 161, 53, 0.3)',
                  boxShadow: '0 12px 40px rgba(203, 161, 53, 0.2)',
                  y: -5
                }}
                transition={{ duration: 0.3 }}
              >
                <div className="shimmer-overlay" />
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-full bg-[#cba135]/10 border border-[#cba135]/20 flex items-center justify-center mx-auto mb-4">
                    <span className="heading-font text-xl font-semibold text-[#cba135]">2</span>
                  </div>
                  <IdentificationCard size={40} weight="duotone" className="text-[#cba135] mx-auto mb-4" />
                  <h3 className="heading-font text-lg font-medium text-[#cba135] mb-3">{t.joinStep2Title}</h3>
                  <p className="text-sm leading-relaxed text-[#cba135]/70">{t.joinStep2Desc}</p>
                </div>
              </motion.div>

              <motion.div
                className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] p-8 rounded-lg text-center relative overflow-hidden"
                whileHover={{ 
                  scale: 1.02,
                  borderColor: 'rgba(203, 161, 53, 0.3)',
                  boxShadow: '0 12px 40px rgba(203, 161, 53, 0.2)',
                  y: -5
                }}
                transition={{ duration: 0.3 }}
              >
                <div className="shimmer-overlay" />
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-full bg-[#cba135]/10 border border-[#cba135]/20 flex items-center justify-center mx-auto mb-4">
                    <span className="heading-font text-xl font-semibold text-[#cba135]">3</span>
                  </div>
                  <Confetti size={40} weight="duotone" className="text-[#cba135] mx-auto mb-4" />
                  <h3 className="heading-font text-lg font-medium text-[#cba135] mb-3">{t.joinStep3Title}</h3>
                  <p className="text-sm leading-relaxed text-[#cba135]/70">{t.joinStep3Desc}</p>
                </div>
              </motion.div>
            </div>
          </motion.div>


          <HallOfFame />


          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-24 max-w-3xl mx-auto"
            data-testid="faq-section"
          >
            <h2 className="heading-font text-3xl sm:text-4xl tracking-tight font-light text-[#cba135] mb-8 text-center">
              {t.faqTitle}
            </h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="faq-1" className="border-[#cba135]/10">
                <AccordionTrigger data-testid="faq-1-trigger" className="text-[#cba135] hover:text-[#cba135] hover:no-underline text-base py-5 [&>svg]:text-[#cba135]">
                  {t.faq1q}
                </AccordionTrigger>
                <AccordionContent className="text-[#cba135]/70 text-base leading-relaxed">
                  {t.faq1a}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-2" className="border-[#cba135]/10">
                <AccordionTrigger data-testid="faq-2-trigger" className="text-[#cba135] hover:text-[#cba135] hover:no-underline text-base py-5 [&>svg]:text-[#cba135]">
                  {t.faq2q}
                </AccordionTrigger>
                <AccordionContent className="text-[#cba135]/70 text-base leading-relaxed">
                  {t.faq2a}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-3" className="border-[#cba135]/10">
                <AccordionTrigger data-testid="faq-3-trigger" className="text-[#cba135] hover:text-[#cba135] hover:no-underline text-base py-5 [&>svg]:text-[#cba135]">
                  {t.faq3q}
                </AccordionTrigger>
                <AccordionContent className="text-[#cba135]/70 text-base leading-relaxed">
                  {t.faq3a}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-5" className="border-[#cba135]/10">
                <AccordionTrigger data-testid="faq-5-trigger" className="text-[#cba135] hover:text-[#cba135] hover:no-underline text-base py-5 [&>svg]:text-[#cba135]">
                  {t.faq5q}
                </AccordionTrigger>
                <AccordionContent className="text-[#cba135]/70 text-base leading-relaxed">
                  {t.faq5a}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-6" className="border-[#cba135]/10">
                <AccordionTrigger data-testid="faq-6-trigger" className="text-[#cba135] hover:text-[#cba135] hover:no-underline text-base py-5 [&>svg]:text-[#cba135]">
                  {t.faq6q}
                </AccordionTrigger>
                <AccordionContent className="text-[#cba135]/70 text-base leading-relaxed">
                  {t.faq6a}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-7" className="border-[#cba135]/10 border-b-0">
                <AccordionTrigger data-testid="faq-7-trigger" className="text-[#cba135] hover:text-[#cba135] hover:no-underline text-base py-5 [&>svg]:text-[#cba135]">
                  {t.faq7q}
                </AccordionTrigger>
                <AccordionContent className="text-[#cba135]/70 text-base leading-relaxed">
                  {t.faq7a}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>

          <footer className="mt-24 pb-12 text-center" data-testid="site-footer">
            <nav className="mb-4 flex flex-wrap justify-center gap-3" aria-label="Legal links">
              {getPolicyLinks(language).map((link) => (
                <Link
                  key={link.slug}
                  to={`/legal/${link.slug}`}
                  className="text-[11px] font-semibold uppercase text-[#cba135]/60 transition-colors hover:text-[#cba135]"
                >
                  {link.title}
                </Link>
              ))}
            </nav>
            {/* 18+ age-restriction badge — gold/brown to match brand */}
            <div
              className="mx-auto mt-2 mb-5 flex h-14 w-14 items-center justify-center rounded-full border-2 border-[#cba135] bg-gradient-to-br from-[#1a1508] to-[#0e0b04] shadow-[0_0_18px_-4px_rgba(203,161,53,0.45)]"
              role="img"
              aria-label="18 plus only — adults only"
              title="Strictly for players aged 18 and older"
              data-testid="footer-age-badge"
            >
              <span className="heading-font text-lg font-bold leading-none tracking-tight text-[#cba135]">
                18<span className="text-[#e6be4b]">+</span>
              </span>
            </div>
            <p className="mx-auto mb-3 max-w-md text-[10px] uppercase tracking-[0.18em] text-[#cba135]/70">
              Strictly 18+ · Play responsibly
            </p>
            <p className="mx-auto max-w-2xl text-[10px] leading-relaxed text-gray-500">
              © 2026 1in2000Club. All rights reserved.
            </p>
          </footer>
        </main>
      </div>

      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="bg-[#0e0b04] border border-[#cba135]/20 backdrop-blur-2xl">
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl text-[#cba135]">
              {authMode === 'login' ? t.welcomeBack : t.joinClub}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={authMode} onValueChange={setAuthMode}>
            <TabsList className="grid w-full grid-cols-2 bg-[#1a1508]">
              <TabsTrigger data-testid="login-tab" value="login" className="data-[state=active]:bg-[#cba135]/10 data-[state=active]:text-[#cba135]">{t.signIn || 'Login'}</TabsTrigger>
              <TabsTrigger data-testid="register-tab" value="register" className="data-[state=active]:bg-[#cba135]/10 data-[state=active]:text-[#cba135]">{t.register}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-[#cba135]">{t.email}</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-[#cba135]">{t.password}</Label>
                  <PasswordInput
                    id="login-password"
                    data-testid="login-password-input"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135]"
                    required
                  />
                </div>
                {requires2FA && (
                  <div data-testid="login-2fa-field">
                    <Label htmlFor="login-totp" className="text-[#cba135]">
                      Two-factor code
                    </Label>
                    <Input
                      id="login-totp"
                      data-testid="login-2fa-input"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      autoComplete="one-time-code"
                      autoFocus
                      placeholder="000000"
                      value={loginData.totpCode}
                      onChange={(e) => setLoginData({...loginData, totpCode: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      className="bg-[#1a1508] border-[#cba135]/40 text-[#cba135] focus:ring-[#cba135] tracking-[8px] text-center font-mono text-lg"
                      required
                    />
                    <p className="text-[10px] text-[#cba135]/55 mt-1.5">
                      Open your authenticator app (Google Authenticator / Authy) and enter the 6-digit code.
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="remember-me"
                      data-testid="remember-me-checkbox"
                      className="border-[#cba135]/30 data-[state=checked]:bg-[#cba135] data-[state=checked]:border-[#cba135]"
                    />
                    <Label htmlFor="remember-me" className="text-sm text-[#cba135]/70 cursor-pointer">{t.rememberMe}</Label>
                  </div>
                  <button 
                    type="button"
                    data-testid="forgot-password-link"
                    className="text-sm text-[#cba135]/70 hover:text-[#cba135] transition-colors"
                    onClick={() => { setShowForgot(true); setForgotSent(false); setForgotEmail(''); }}
                  >
                    {t.forgotPassword}
                  </button>
                </div>
                <Button 
                  data-testid="login-submit-button"
                  type="submit" 
                  className="w-full bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold"
                  disabled={loading}
                >
                  {loading ? 'Loading...' : t.login}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                {registerData.affiliateCode && (
                  <div
                    data-testid="affiliate-banner"
                    className="bg-gradient-to-r from-[#cba135]/15 via-[#cba135]/5 to-transparent border border-[#cba135]/30 rounded-lg px-3 py-2.5 flex items-center gap-3"
                  >
                    <div className="shrink-0 h-8 w-8 rounded-full bg-[#0e0b04] border border-[#cba135]/40 flex items-center justify-center shadow-[0_0_12px_rgba(203,161,53,0.25)]">
                      <LinkSimple size={16} weight="bold" className="text-[#e6be4b]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#cba135] leading-tight">
                        <span className="font-semibold tracking-wide">Partner link active</span>
                        <span className="text-[#cba135]/60"> — referred by </span>
                        <code className="bg-[#0e0b04] border border-[#cba135]/20 px-1.5 py-0.5 rounded text-[#e6be4b] font-mono text-[11px]">{registerData.affiliateCode}</code>
                      </p>
                    </div>
                  </div>
                )}
                <div>
                  <Label htmlFor="register-username" className="text-[#cba135]">{t.nick}</Label>
                  <Input
                    id="register-username"
                    data-testid="register-username-input"
                    type="text"
                    value={registerData.username}
                    onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                    className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-[#cba135]">{t.email}</Label>
                  <Input
                    id="register-email"
                    data-testid="register-email-input"
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="register-phone" className="text-[#cba135]">{t.phone}</Label>
                  <Input
                    id="register-phone"
                    data-testid="register-phone-input"
                    type="tel"
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                    className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135]"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-[#cba135]">{t.password}</Label>
                  <PasswordInput
                    id="register-password"
                    data-testid="register-password-input"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                    className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135]"
                    required
                  />
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="register-terms"
                    data-testid="register-terms-checkbox"
                    checked={registerData.termsAccepted}
                    onCheckedChange={(checked) => setRegisterData({...registerData, termsAccepted: Boolean(checked)})}
                    className="mt-0.5 border-[#cba135]/30 data-[state=checked]:bg-[#cba135] data-[state=checked]:border-[#cba135]"
                  />
                  <Label htmlFor="register-terms" className="text-sm text-[#cba135]/70 cursor-pointer leading-relaxed">
                    {t.registerTermsPrefix}{" "}
                    <a
                      href="/legal/terms"
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#cba135] underline underline-offset-4"
                    >
                      {t.registerTermsTerms}
                    </a>
                    ,{" "}
                    <a
                      href="/legal/privacy"
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#cba135] underline underline-offset-4"
                    >
                      {t.registerTermsPrivacy}
                    </a>
                    {" "}{t.registerTermsAnd}{" "}
                    <a
                      href="/legal/kyc-aml"
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#cba135] underline underline-offset-4"
                    >
                      {t.registerTermsKyc}
                    </a>
                    .
                  </Label>
                </div>
                <Button 
                  data-testid="register-submit-button"
                  type="submit" 
                  className="w-full bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold"
                  disabled={loading || !registerData.termsAccepted}
                >
                  {loading ? t.creatingAccount : t.startRegistration}
                </Button>
              </form>
              <div className="mt-5 pt-4 border-t border-[#cba135]/10" data-testid="registration-steps">
                <div className="space-y-2">
                  {[
                    { num: 1, text: t.regFlowStep1, active: true },
                    { num: 2, text: t.regFlowStep2 },
                    { num: 3, text: t.regFlowStep3 },
                    { num: 4, text: t.regFlowStep4 },
                  ].map((step) => (
                    <div key={step.num} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold ${step.active ? 'bg-[#cba135] text-[#0e0b04]' : 'border border-[#cba135]/30 text-[#cba135]/50'}`}>
                        {step.num}
                      </div>
                      <span className={`text-xs ${step.active ? 'text-[#cba135]' : 'text-[#cba135]/50'}`}>{step.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog open={showVerification} onOpenChange={setShowVerification}>
        <DialogContent className="bg-[#0e0b04] border-[#cba135]/20 max-w-md" data-testid="verification-dialog">
          <VerificationWizard
            token={pendingToken}
            language={language}
            onComplete={async () => {
              setShowVerification(false);
              if (pendingToken) {
                try {
                  const res = await fetch(`${API}/auth/me`, {
                    headers: { 'Authorization': `Bearer ${pendingToken}` }
                  });
                  const user = await readJsonOnce(res);
                  if (res.ok) {
                    onLogin(pendingToken, user);
                    toast.success('Welcome!');
                  }
                } catch (verifyMeErr) {
                  console.warn('Post-verification /auth/me fetch failed:', verifyMeErr);
                }
              }
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent className="bg-gradient-to-br from-[#1a1508] to-[#0e0b04] border-[#cba135]/20 max-w-sm">
          <DialogHeader>
            <DialogTitle className="heading-font text-xl text-[#cba135]">{t.forgotPassword}</DialogTitle>
          </DialogHeader>
          {forgotSent ? (
            <div className="text-center py-4">
              <p className="text-[#cba135]/80 text-sm">{language === 'sk' ? 'Ak tento email existuje, bol na neho odoslan\u00FD odkaz na obnovu hesla.' : language === 'de' ? 'Wenn diese E-Mail existiert, wurde ein Link zum Zur\u00FCcksetzen gesendet.' : 'If that email exists, a reset link has been sent.'}</p>
            </div>
          ) : (
            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoading(true);
              try {
                await fetch(`${API}/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: forgotEmail }) });
              } catch (forgotErr) {
                console.warn('Forgot password request failed:', forgotErr);
              }
              setForgotSent(true);
              setLoading(false);
            }} className="space-y-4 mt-2">
              <div>
                <Label className="text-[#cba135]/70 text-sm">{t.email}</Label>
                <Input data-testid="forgot-email-input" type="email" value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)} required
                  className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135]" />
              </div>
              <Button data-testid="forgot-submit-button" type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold">
                {loading ? '...' : language === 'sk' ? 'Odosla\u0165 odkaz' : language === 'de' ? 'Link senden' : 'Send reset link'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Landing;
